import { isPackageEditable } from '@/helpers/packageEditability';
import { useEditAccountMutation, useGetAccountQuery } from '@/services/accounts';
import { useGetDocumentsQuery } from '@/services/documents';
import { useGetQueueByAccountIdAndTypeQuery } from '@/services/queues';
import { ReviewPackageActions } from '@/types/ReviewPackage';
import {
  Box,
  BoxProps,
  Button,
  FormikCheckboxInput,
  FormikSelectInputNative,
  FormikTextInput,
  Spinner,
  TextLink,
  Toggle,
} from '@palmetto/palmetto-components';
import { Field, Formik } from 'formik';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  EnergyBackupExistingSystemFinanceType,
  ProgramType,
  QueueType,
  VerifyExistingSystemDocumentTypes,
} from 'types';

import { useAuth } from '../auth/authProvider';
import { isInternalUser } from '../auth/RequireIsInternalUser';
import { EnhancedFormikForm } from '../EnhancedFormikForm';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import { DocumentList } from '../MilestonePackagePartials/Documents/DocumentList';
import PageHeader from '../PageHeader';

const existingSystemFinanceTypeOptions = [
  {
    label: 'Cash',
    value: EnergyBackupExistingSystemFinanceType.cash,
  },
  {
    label: 'Lease/PPA',
    value: EnergyBackupExistingSystemFinanceType.leasePPA,
  },
  {
    label: 'Loan',
    value: EnergyBackupExistingSystemFinanceType.loan,
  },
  {
    label: 'Unknown',
    value: EnergyBackupExistingSystemFinanceType.unknown,
  },
];

const boxStyle: BoxProps = {
  direction: {
    base: 'column',
    tablet: 'row',
  },
  alignItems: {
    base: 'stretch',
    tablet: 'flex-end',
  },
  justifyContent: {
    tablet: 'flex-end',
  },
  childGap: 'sm',
  style: { flexShrink: 0 },
  position: 'relative',
  padding: 'lg 0 0 0',
};

export const VerifyExistingSystem = () => {
  const navigate = useNavigate();
  const { claims } = useAuth();

  const { id: accountId } = useParams<{ id: any }>();
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(accountId);
  const { data: existingSystemPhotos } = useGetDocumentsQuery({
    accountId,
    documentTypeFilter: [VerifyExistingSystemDocumentTypes.existingSystemPhotos],
  });
  const { data: queue, isLoading: isQueueLoading } = useGetQueueByAccountIdAndTypeQuery({
    accountId,
    type: QueueType.installPackage,
  });

  const [editAccount] = useEditAccountMutation();

  const primaryApplicantName = account?.primaryApplicantName;
  if (account && account?.programType !== ProgramType.energyBackup) {
    navigate(`/accounts/${accountId}`);
  }
  const isFinCoUser = isInternalUser(claims, true);
  const canEditPackage = isPackageEditable({
    queue,
    isFinCoUser,
  });
  const isLoading = isAccountLoading || isQueueLoading;

  const handleSubmit = async (values: any) => {
    if (values.hasExistingSystem) {
      if (
        !values.acSystemSize ||
        !values.financeType ||
        !existingSystemPhotos?.length ||
        !values.acSystemSizeAttestationCheckbox
      ) {
        const confirmed = window.confirm(
          'AC System Size, Finance Type, System Size attestation, and Existing Solar System Documents will be required before submitting for install. Do you wish to proceed?',
        );
        if (!confirmed) return;
      }
    }
    const { acSystemSizeAttestationCheckbox, ...rest } = values;
    await editAccount({
      id: accountId,
      account: {
        existingSystemDetails: {
          ...(account?.existingSystemDetails || {}),
          ...rest,
          acSystemSizeAttestation: acSystemSizeAttestationCheckbox
            ? {
                attestmentText: 'The system size is within OEM limits for the battery/batteries being installed.',
              }
            : undefined,
        },
      },
    }).unwrap();
    navigate(`/accounts/${accountId}/installation-package/${ReviewPackageActions.create}`, {
      state: { fromVerifyExistingSystem: true },
    });
  };

  return (
    <>
      <Helmet>Verify Existing Equipment</Helmet>
      <PageHeader
        eyebrow={<Link to={`/accounts/${accountId}`}>{primaryApplicantName}</Link>}
        title="Verify Existing System"
      />
      <MainContainer>
        {isLoading && (
          <Box padding="lg">
            <Spinner size="lg" />
          </Box>
        )}
        {!isLoading && (
          <Formik
            initialValues={{
              hasExistingSystem: account?.existingSystemDetails?.hasExistingSystem || false,
              acSystemSize: account?.existingSystemDetails?.acSystemSize || '',
              financeType: account?.existingSystemDetails?.financeType || '',
              acSystemSizeAttestationCheckbox:
                Boolean(account?.existingSystemDetails?.acSystemSizeAttestation?.attestmentText) || false,
            }}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            validateOnChange={true}
          >
            {({ values, setFieldValue, isSubmitting }) => (
              <EnhancedFormikForm noValidate>
                <Box padding="lg">
                  <FormSection title="Confirm Existing Solar System">
                    <Toggle
                      id="hasExistingSystem"
                      label="Energy Backup installation is being connected to an Existing Solar System"
                      isChecked={values.hasExistingSystem}
                      onChange={(event) => {
                        setFieldValue('hasExistingSystem', event.target.checked);
                      }}
                      isDisabled={!canEditPackage}
                    />
                  </FormSection>
                  {values.hasExistingSystem && (
                    <>
                      <FormSection
                        title="Existing Solar System Details"
                        description={
                          <Box gap="xs">
                            <Box>Note: The system size must be provided in AC kW.</Box>
                            <Box>Please provide all required details of existing solar system.</Box>
                            <Box display="block">
                              For full details on these requirements please visit the{' '}
                              <TextLink
                                href="https://eb.palmetto.finance/en/articles/12015351-energy-backup-technical-considerations-for-business-planning-sales"
                                target="_blank"
                                rel="noreferrer"
                              >
                                Knowledge Base
                              </TextLink>
                            </Box>
                          </Box>
                        }
                      >
                        <Field
                          type="number"
                          label="AC System Size (kW)"
                          name="acSystemSize"
                          id="acSystemSize"
                          required
                          component={FormikTextInput}
                          maxWidth="3xl"
                          isDisabled={!canEditPackage}
                        />
                        <Field
                          type="text"
                          component={FormikCheckboxInput}
                          label={
                            <Box display="block">
                              I confirm that the system size is within{' '}
                              <TextLink
                                href="https://help.palmetto.finance/en/articles/12015351-energy-backup-technical-considerations-for-business-planning-sales#h_e8b3d384c8"
                                target="_blank"
                                rel="noreferrer"
                              >
                                OEM limits
                              </TextLink>{' '}
                              for the battery/batteries being installed.
                            </Box>
                          }
                          id="acSystemSizeAttestationCheckbox"
                          name="acSystemSizeAttestationCheckbox"
                          value={values.acSystemSizeAttestationCheckbox}
                          isDisabled={!canEditPackage}
                        />
                        <DocumentList
                          title="Existing Solar System Documents"
                          documentTypeFilter={[VerifyExistingSystemDocumentTypes.existingSystemPhotos]}
                          showTypeLabelOnItems={false}
                          baseCategory="Existing System Photos"
                          grouped={false}
                          isPackageEditable={canEditPackage}
                        />
                        <Box gap="xs">
                          <Field
                            label="Finance Type"
                            name="financeType"
                            id="financeType"
                            required
                            component={FormikSelectInputNative}
                            options={existingSystemFinanceTypeOptions}
                            maxWidth="3xl"
                            isDisabled={!canEditPackage}
                          />
                          <Box fontSize="sm">
                            <i>
                              Note: There are no finance type limitations for adding battery to an existing Solar
                              system.
                            </i>
                          </Box>
                        </Box>
                      </FormSection>
                    </>
                  )}
                  <Box
                    {...boxStyle}
                    width={{
                      base: '100',
                      tablet: 'auto',
                    }}
                  >
                    <Button
                      onClick={() => navigate(`/accounts/${accountId}`)}
                      variant="secondary"
                      tone="neutral"
                      isLoading={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      isLoading={isSubmitting}
                      type="submit"
                      iconSuffix="arrow-right"
                      isDisabled={!canEditPackage}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              </EnhancedFormikForm>
            )}
          </Formik>
        )}
      </MainContainer>
    </>
  );
};
